// frontend/mobile/src/pages/VehicleInfo.tsx
// D2: 車両情報画面 - データベース連携版

import React, { useState, useEffect } from 'react';
import { useTLog } from '../hooks/useTLog';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useOperationStore } from '../stores/operationStore';

import { 
  Truck, 
  ArrowRight, 
  ArrowLeft, 
  Loader2,
  User,
  Calendar,
  Gauge
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import apiService from '../services/api';
import axios from 'axios';

// ✅ 修正: バックエンドAPIのレスポンス型に合わせる
interface VehicleData {
  id: string;  // UUID形式
  plateNumber: string;
  vehicleType: string;
  model: string;
  manufacturer: string;
  currentMileage: number;
  capacity?: number;         // REQ-004: 積載量
  capacityTons?: number;     // REQ-004: 積載量(別名)
  inspectionExpiry?: string; // REQ-007: 車検期限
  status: string;
  notes?: string;
  loadingPattern?: number;   // 🆕 オペレーションパターン
  unloadingPattern?: number; // 🆕 オペレーションパターン
}

// ✅ 修正: 表示用の車両データ型
interface VehicleDisplay {
  id: string;
  vehicleNumber: string;  // 表示用(車番)
  vehicleType: string;
  currentMileage: number;
  capacity?: number;       // REQ-004: 積載量
  inspectionExpiry?: string; // REQ-007: 車検期限 (ISO date string)
  status: string;          // 🆕 車両ステータス追加 (ACTIVE / MAINTENANCE / INACTIVE / RETIRED)
  lastDriver?: string;
  lastOperationDate?: string;
  loadingPattern?: number;   // 🆕 オペレーションパターン
  unloadingPattern?: number; // 🆕 オペレーションパターン
}

const VehicleInfo: React.FC = () => {
  useTLog('VEHICLE_INFO', '車両情報');

  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthStore();

  // 別名をつけて Store の関数を取得
  const { setVehicleInfo: saveVehicleToStore, setDriverInfo: saveDriverToStore, setCustomerInfo: saveCustomerToStore } = useOperationStore();
  
  const [vehicles, setVehicles] = useState<VehicleDisplay[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [vehicleInfo, setVehicleInfo] = useState<VehicleDisplay | null>(null);
  const [startMileage, setStartMileage] = useState('');
  const [isFetching, setIsFetching] = useState(true);

  // 🆕 客先選択
  const [customers, setCustomers] = useState<{ id: string; name: string }[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [isCustomerFetching, setIsCustomerFetching] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    fetchVehicles();
  }, []);

  // 🆕 客先一覧取得
  useEffect(() => {
    const fetchCustomers = async () => {
      setIsCustomerFetching(true);
      try {
        const res = await apiService.getCustomers();
        if (res.success) {
          // 二重ネスト対応
          const inner = res.data?.data || res.data;
          const list = inner?.customers || (Array.isArray(inner) ? inner : []);
          setCustomers(Array.isArray(list) ? list : []);
        }
      } catch (e) {
        console.error('客先取得エラー:', e);
      } finally {
        setIsCustomerFetching(false);
      }
    };
    fetchCustomers();
  }, []);

  // ✅ 修正: 実際にAPIから車両データを取得
  const fetchVehicles = async () => {
    setIsFetching(true);
    try {
      console.log('🔍 車両情報を取得中...');
      console.log('📡 API Base URL:', import.meta.env.VITE_API_BASE_URL);
      
      // ✅ 修正: axiosを直接使用してAPIを呼び出す
      const baseURL = import.meta.env.VITE_API_BASE_URL || 'https://192.168.1.12:8443/api/v1';
      const token = apiService.getToken();
      
      const axiosInstance = axios.create({
        baseURL,
        timeout: 15000,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      
      // GET /api/v1/mobile/vehicles (モバイル専用 - 高速)
      const response = await axiosInstance.get('/mobile/vehicles', {
        params: {
          page: 1,
          limit: 100
        }
      });
      
      console.log('📦 APIレスポンス:', response.data);
      
      if (response.data.success && response.data.data) {
        // ✅ バックエンドからのデータを変換
        // mobileController: sendSuccess → { success, data: { vehicles:[...], pagination:{} } }
        const apiVehicles = response.data.data.vehicles
          || (Array.isArray(response.data.data) ? response.data.data : null);
        
        if (!Array.isArray(apiVehicles)) {
          console.error('❌ 車両データが配列ではありません:', apiVehicles);
          throw new Error('車両データの形式が不正です');
        }
        
        // ✅ APIレスポンスを表示用データに変換（lastDriver/lastOperationDateをAPIから直接取得）
        const vehicleList: VehicleDisplay[] = apiVehicles.map((v: VehicleData) => {
          return {
            id: v.id,  // ✅ UUID形式のIDをそのまま使用
            vehicleNumber: v.plateNumber,  // 車番(ナンバープレート)
            vehicleType: v.vehicleType || v.model || '未設定',  // 🆕 フォールバック追加
            currentMileage: v.currentMileage,
            capacity: v.capacity ?? v.capacityTons ?? undefined,  // REQ-004
            inspectionExpiry: v.inspectionExpiry ?? undefined,  // REQ-007
            status: v.status,  // 🆕 ステータスをそのまま保持
            // ✅ BUG-048修正: notesテキスト解析を廃止→APIから直接取得
            loadingPattern: (v as any).loadingPattern ?? 2,
            unloadingPattern: (v as any).unloadingPattern ?? 2,
            lastDriver: (v as any).lastDriver ?? '未割当',
            lastOperationDate: (v as any).lastOperationDate ?? '－'
          };
        });
        
        console.log('✅ 車両リスト取得成功:', vehicleList);
        setVehicles(vehicleList);
        
        // ✅ ユーザーに割り当てられた車両がある場合は自動選択
        if (user?.vehicleId && vehicleList.length > 0) {
          const assignedVehicle = vehicleList.find(v => v.id === user.vehicleId);
          if (assignedVehicle) {
            setSelectedVehicleId(assignedVehicle.id);
            setVehicleInfo(assignedVehicle);
            setStartMileage(assignedVehicle.currentMileage.toString());
          }
        }
      } else {
        console.error('❌ APIレスポンスが不正:', response.data);
        throw new Error('車両データの取得に失敗しました');
      }
      
    } catch (error: any) {
      console.error('❌ 車両情報取得エラー:', error);
      
      // ✅ エラーメッセージを詳細に表示
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error(
          'サーバーの応答がタイムアウトしました。\n' +
          'バックエンドサーバーの状態を確認してください。',
          { duration: 6000 }
        );
      } else if (error.message?.includes('Network Error') || error.message?.includes('ERR_CONNECTION_REFUSED')) {
        toast.error(
          'サーバーに接続できません。\n' +
          'バックエンドサーバーが起動しているか確認してください。',
          { duration: 6000 }
        );
      } else if (error.response?.status === 401) {
        toast.error('認証エラー: 再ログインが必要です');
        logout();
        navigate('/login', { replace: true });
        return;
      } else if (error.response?.status === 404) {
        toast.error('車両情報のAPIエンドポイントが見つかりません');
      } else {
        toast.error(
          `車両情報の取得に失敗しました\n${error.message}`,
          { duration: 6000 }
        );
      }
      
      // ✅ エラー時は空のリストを設定
      setVehicles([]);
      
    } finally {
      setIsFetching(false);
    }
  };

  // ✅ 車両選択時の処理
  const handleVehicleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const vehicleId = e.target.value;

    // 🆕 選択不可ステータスの車両は弾く（disabled optionの二重チェック）
    const selected = vehicles.find(v => v.id === vehicleId);
    if (selected && selected.status !== 'ACTIVE') {
      toast.error('この車両は現在選択できません');
      return;
    }

    setSelectedVehicleId(vehicleId);
    
    if (selected) {
      setVehicleInfo(selected);
      setStartMileage(selected.currentMileage.toString());
      // REQ-007: 車検期限60日以内警告
      if (selected.inspectionExpiry) {
        const expiry = new Date(selected.inspectionExpiry);
        const today = new Date();
        const diffDays = Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) {
          toast.error(
            `🚨 車検切れ！\nこの車両は車検が切れています\n期限: ${expiry.toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })}\n\n⚠️ 直ちに運行を中止してください`,
            {
              duration: 15000,
              style: {
                fontSize: '16px',
                fontWeight: 'bold',
                padding: '20px 24px',
                maxWidth: '380px',
                lineHeight: '1.8',
                background: '#7f1d1d',
                color: '#fff',
                border: '3px solid #ef4444',
                borderRadius: '12px',
              },
            }
          );
        } else if (diffDays <= 60) {
          const isUrgent = diffDays <= 14;
          toast(
            `${isUrgent ? '🔴' : '🟡'} 車検期限まで残り ${diffDays} 日\n期限: ${expiry.toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })}\n${isUrgent ? '\n⚠️ 至急、車検の手続きをしてください！' : '\n早めに車検の手続きをしてください。'}`,
            {
              duration: isUrgent ? 15000 : 10000,
              icon: isUrgent ? '🚗' : '📋',
              style: {
                fontSize: isUrgent ? '15px' : '14px',
                fontWeight: 'bold',
                padding: '20px 24px',
                maxWidth: '380px',
                lineHeight: '1.8',
                background: isUrgent ? '#7c2d12' : '#78350f',
                color: '#fff',
                border: isUrgent ? '3px solid #f97316' : '2px solid #f59e0b',
                borderRadius: '12px',
              },
            }
          );
        }
      }
    } else {
      setVehicleInfo(null);
      setStartMileage('');
    }
  };

  const handleMileageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setStartMileage(value);
  };

  const validateForm = (): boolean => {
    if (!selectedCustomerId) {
      toast.error('客先を選択してください');
      return false;
    }

    if (!selectedVehicleId) {
      toast.error('車番を選択してください');
      return false;
    }
    
    // ✅ BUG-FIX: 0km新車対応 - 空文字のみエラー（0は有効な開始距離）
    if (startMileage === '') {
      toast.error('開始距離を入力してください');
      return false;
    }
    
    if (vehicleInfo && parseInt(startMileage) < vehicleInfo.currentMileage) {
      toast.error('開始距離は前回終了距離以上である必要があります');
      return false;
    }
    
    return true;
  };

  const handleNext = () => {
    if (!validateForm()) {
      return;
    }
    
    // ✅ operationStoreに車両情報を保存（sessionStorageは使わない）
    if (vehicleInfo) {
      saveVehicleToStore({
        vehicleId: selectedVehicleId,
        vehicleNumber: vehicleInfo.vehicleNumber,
        vehicleType: vehicleInfo.vehicleType,
        startMileage: parseInt(startMileage),
        capacity: vehicleInfo.capacity,  // REQ-004: 積載量をStoreに保存
        loadingPattern: vehicleInfo.loadingPattern ?? 2,   // 🆕
        unloadingPattern: vehicleInfo.unloadingPattern ?? 2, // 🆕
      });
      
      if (user) {
        saveDriverToStore({
          driverId: user.id,
          driverName: user.name
        });
      }
      
      const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
      if (selectedCustomer) {
        saveCustomerToStore({
          customerId: selectedCustomer.id,
          customerName: selectedCustomer.name,
        });
      }

      toast.success('車両情報を保存しました');
      navigate('/pre-departure-inspection');
    } else {
      toast.error('車両情報の取得に失敗しました');
    }
  };

  // ✅ 修正: ログアウトせずにHome画面に戻る
  const handleBack = () => {
    navigate('/home');
  };

  // ✅ ローディング中の表示
  if (isFetching) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">車両情報を読み込み中...</p>
        </div>
      </div>
    );
  }

  // ✅ 車両データが取得できなかった場合の表示
  if (vehicles.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
          <div className="max-w-md mx-auto px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Truck className="w-7 h-7" />
                <h1 className="text-xl font-bold">車両情報</h1>
              </div>
              <div className="text-sm text-blue-100">
                <User className="w-4 h-4 inline mr-1" />
                {user?.name}
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-md mx-auto px-6 py-8">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="text-center py-8">
              <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">利用可能な車両がありません</p>
              <button
                onClick={() => fetchVehicles()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                再読み込み
              </button>
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl
                hover:bg-gray-300 transition-colors duration-200 font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>戻る</span>
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
        <div className="max-w-md mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Truck className="w-7 h-7" />
              <h1 className="text-xl font-bold">車両情報</h1>
            </div>
            <div className="text-sm text-blue-100">
              <User className="w-4 h-4 inline mr-1" />
              {user?.name}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 space-y-6">
          {/* 🆕 客先選択 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              客先 <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedCustomerId}
              onChange={e => setSelectedCustomerId(e.target.value)}
              className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                transition-all duration-200 text-gray-800 font-medium cursor-pointer"
            >
              <option value="">
                {isCustomerFetching ? '読み込み中...' : '客先を選択してください'}
              </option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>

          {/* ✅ 車番選択 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              車番
            </label>
            <select
              value={selectedVehicleId}
              onChange={handleVehicleChange}
              className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                transition-all duration-200 text-gray-800 font-medium cursor-pointer"
            >
              <option value="">車両を選択してください</option>
              {vehicles.map(vehicle => {
                const isSelectable = vehicle.status === 'ACTIVE';
                const statusLabel =
                  vehicle.status === 'MAINTENANCE' ? ' 【メンテ中】' :
                  vehicle.status === 'INACTIVE'    ? ' 【非稼働】' :
                  vehicle.status === 'RETIRED'     ? ' 【廃車】' :
                  '';
                return (
                  <option
                    key={vehicle.id}
                    value={vehicle.id}
                    disabled={!isSelectable}
                    style={{ color: isSelectable ? 'inherit' : '#9ca3af' }}
                  >
                    {vehicle.vehicleNumber}{statusLabel}
                  </option>
                );
              })}
            </select>
          </div>

          {/* ✅ 選択した車両の詳細情報 */}
          {vehicleInfo && (
            <>
              <div className="animate-fade-in">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  車種
                </label>
                <div className="px-4 py-3.5 bg-blue-50 border-2 border-blue-200 rounded-xl
                  text-gray-800 font-medium flex items-center justify-between">
                  <span>{vehicleInfo.vehicleType}</span>
                  <Truck className="w-5 h-5 text-blue-600" />
                </div>
              </div>

              <div className="animate-fade-in">
                <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <Gauge className="w-4 h-4 mr-2 text-gray-600" />
                  開始距離 (km)
                </label>
                <input
                  type="text"
                  value={startMileage}
                  onChange={handleMileageChange}
                  placeholder="開始距離を入力"
                  className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                    transition-all duration-200 text-gray-800 font-medium"
                />
                <p className="mt-2 text-sm text-gray-500">
                  前回終了距離: <span className="font-semibold">{vehicleInfo.currentMileage.toLocaleString()} km</span>
                </p>
              </div>

              <div className="animate-fade-in grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">運転手名</label>
                  <div className="text-sm font-medium text-gray-800">
                    {user?.name || '未設定'}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1 flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    前回運転手
                  </label>
                  <div className="text-sm font-medium text-gray-800">
                    {vehicleInfo.lastDriver || '－'}
                  </div>
                </div>
              </div>

              {vehicleInfo.lastOperationDate && (
                <div className="animate-fade-in">
                  <label className="block text-xs text-gray-500 mb-1">最終運行日</label>
                  <div className="text-sm font-medium text-gray-800">
                    {vehicleInfo.lastOperationDate}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ✅ 進む・戻るボタン */}
        <div className="mt-6 flex justify-between">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl
              hover:bg-gray-300 transition-colors duration-200 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>戻る</span>
          </button>

          <button
            onClick={handleNext}
            disabled={!selectedVehicleId || startMileage === ''}
            className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700
              text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200
              font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>進む</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </main>
    </div>
  );
};

export default VehicleInfo;